import { secureStorage } from './secureStorage';

export const LICENSE_VALIDATION_CACHE_KEY = 'licenseValidationCache';

export interface LicenseValidationCache {
  licenseId?: number;
  hardDiskId: string;
  deviceId?: string;
  status: string;
  isValid: boolean;
  remainingDays?: number;
  expiryDate?: string;
  daysCount?: number;
  startDate?: string;
  companyName?: string;
  licenseName?: string;
  approvalStatus?: string;
  message?: string;
  lastSuccessfulValidation?: string;
  nextValidationTime?: string;
  validationIntervalHours?: number;
}

function isHardFailStatus(status?: string): boolean {
  const s = (status || '').toLowerCase();
  return (
    s === 'expired' ||
    s === 'revoked' ||
    s === 'invalid' ||
    s === 'deleted' ||
    s === 'rejected' ||
    s === 'suspended' ||
    s === 'devicenotauthorized' ||
    s === 'device not authorized'
  );
}

export function readLicenseValidationCache(hardDiskId: string): LicenseValidationCache | null {
  try {
    const raw = secureStorage.getItem(LICENSE_VALIDATION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LicenseValidationCache;
    if (!parsed || parsed.hardDiskId !== hardDiskId) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeLicenseValidationCache(cache: LicenseValidationCache): void {
  try {
    const payload: LicenseValidationCache = {
      licenseId: cache.licenseId,
      hardDiskId: cache.hardDiskId,
      deviceId: cache.deviceId,
      status: cache.status || cache.approvalStatus || '',
      isValid: cache.isValid,
      remainingDays: cache.remainingDays,
      expiryDate: cache.expiryDate,
      daysCount: cache.daysCount,
      startDate: cache.startDate,
      companyName: cache.companyName,
      licenseName: cache.licenseName,
      approvalStatus: cache.approvalStatus,
      message: cache.message,
      lastSuccessfulValidation: cache.lastSuccessfulValidation || new Date().toISOString(),
      nextValidationTime: cache.nextValidationTime,
      validationIntervalHours: cache.validationIntervalHours || 24,
    };
    if (!payload.nextValidationTime) {
      const hours = payload.validationIntervalHours || 24;
      payload.nextValidationTime = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    }
    secureStorage.setItem(LICENSE_VALIDATION_CACHE_KEY, JSON.stringify(payload));
  } catch {
    // ignore storage failures
  }
}

export function clearLicenseValidationCache(): void {
  try {
    secureStorage.removeItem(LICENSE_VALIDATION_CACHE_KEY);
  } catch {
    // ignore
  }
}

export function isLicenseCacheStillValid(cache: LicenseValidationCache | null): boolean {
  if (!cache) return false;
  if (isHardFailStatus(cache.status || cache.approvalStatus)) return false;
  if (cache.expiryDate) {
    const expiry = new Date(cache.expiryDate).getTime();
    if (!Number.isNaN(expiry) && expiry < Date.now()) return false;
  }
  return cache.isValid === true && (cache.approvalStatus === 'Approved' || cache.status === 'Approved');
}

export function shouldRefreshLicenseCache(cache: LicenseValidationCache | null): boolean {
  if (!cache?.nextValidationTime) return true;
  const next = new Date(cache.nextValidationTime).getTime();
  if (Number.isNaN(next)) return true;
  return Date.now() >= next;
}
